import React, { useState } from "react";
import {
  Button,
  Text,
  FormLabel,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import moment from "moment";

import { UseUserContext } from '../context/UserContext';
import WeightInput from "./WeightInput";
import DateInput from "./DateInput";
import { CalcBMR, CalcCalorieMax } from "../bmr/bmr";
import { sort } from "../utils/utils";
import { interpolateWeightLogHistory } from "../bmr/bmr";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase/firebase";

export default function GoalForm(): JSX.Element {

  const { currentUser, setTempUserData, tempUserData, updateCurrentUserData } = UseUserContext();
  const userData = tempUserData || currentUser.data;

  const userWeightLogRaw = currentUser.weight_log || { };
  let userWeightLog = userWeightLogRaw;
  if (userWeightLog.history) {
    sort(userWeightLog.history, "date");
  }
  const userWeightLogHistory = interpolateWeightLogHistory(userWeightLog.history || []);
  const lastLog = userWeightLogHistory.slice(userWeightLogHistory.length-1, userWeightLogHistory.length)[0];
  const trueWeight = lastLog?.trueWeight;

  const measuredWeight = String(userData.last_weight_lbs || "");
  const [ userWeightGoal, setUserWeightGoal ] = useState(String(Number(userData.goal_weight_lbs) || ""));
  const [ userGoalDate, setUserGoalDate ] = useState(String(userData.goal_end_date || ""));
  const userGoalDateMoment = moment(userGoalDate);
  const isValidWeight = Number(userWeightGoal) > 5;

  // TBD use true weight here

  const userBMR = CalcBMR(Number(trueWeight || measuredWeight), userData);

  const userCalorieMax = CalcCalorieMax(Number(trueWeight || measuredWeight), Number(userWeightGoal), { ...userData, goal_end_date: userGoalDate });

  // console.log("userData: ",userData);
  // console.log(`userCalorieMax: ${userCalorieMax} for weight ${measuredWeight} and goal ${userWeightGoal}`);

  const calorieDelta = userBMR - (userCalorieMax ?? NaN);
  const poundsPerDay = Math.abs(calorieDelta/3500);
  const poundsPerWeek = poundsPerDay * 7;
  const weightWarning = poundsPerWeek > 2;
  const validGoalDate = !!moment(userGoalDate).isAfter(moment());

  let validCalorieDelta = false;
  if (userCalorieMax !== null && Number.isFinite(calorieDelta)) validCalorieDelta = true;

  const onSaveChanges = () => {
    if (!isValidWeight || !validGoalDate || !validCalorieDelta || weightWarning) return;
    updateCurrentUserData(currentUser.auth.uid!, { ...userData, goal_weight_lbs: Number(userWeightGoal), goal_end_date: userGoalDate });
    if (!currentUser.data.goal_weight_lbs) {
      console.log("Completing goal setting for the first time");
      logEvent(analytics, 'profile_onboarded_goal', {
        uid: currentUser.auth.uid,
        currentWeight: Number(measuredWeight),
        goalWeight: Number(userWeightGoal),
        goalDate: userGoalDate
      });
    } else {
      logEvent(analytics, 'goal_updated', {
        uid: currentUser.auth.uid,
        currentWeight: Number(measuredWeight),
        goalWeight: Number(userWeightGoal),
        goalDate: userGoalDate
      });
    }
  }

  const onSetWeightGoal = (newVal: string, db_field: string) => {
    setTempUserData({ [db_field]: Number(newVal) });
    setUserWeightGoal(newVal);
  }

  const onSetGoalDate = (newVal: string, db_field: string) => {
    setTempUserData({ [db_field]: newVal });
    setUserGoalDate(newVal);
  }

  return (
    <React.Fragment>
      
      <FormLabel>Target weight (lbs)</FormLabel>
      { !isValidWeight && <Text color={"red"}>Please enter a valid weight (in pounds)</Text>}
      {WeightInput(userWeightGoal, onSetWeightGoal, "goal_weight_lbs", "Enter your weight goal (in pounds)")}

      <FormLabel>Target completion date</FormLabel>
      {DateInput(userGoalDate, onSetGoalDate, "goal_end_date", "Enter your target goal completion date (YYYY-MM-DD)")}

      <Text fontSize={'sm'} color={'gray.600'} align="center">Your goal is to hit <strong>{userWeightGoal}</strong> lbs by <strong>{userGoalDateMoment.format("MMMM Do YYYY")}</strong></Text>

      {userCalorieMax === null ? (
        <Text role="alert" color="red.700">Basal can’t suggest a calorie target for these details. Check your profile, choose a closer weight goal or a later date, and ask a qualified professional for help choosing an appropriate plan.</Text>
      ) : (
        <Stat>
          <StatLabel>Calorie Max (estimate)</StatLabel>
          <StatNumber>{userCalorieMax.toLocaleString()} Calories</StatNumber>
          <StatHelpText>A starting estimate, not a measurement of your metabolism or a guarantee of reaching your goal.</StatHelpText>
        </Stat>
      )}

      { !!validGoalDate ? (
        <Text>Looks like your goal is coming up about <strong>{userGoalDateMoment.fromNow(true)}</strong> from now. You ready for this?</Text>
      ) : (
        <Text fontSize={'sm'} color={'red'}>Hey, please double check your goal date. It looks like it isn't entered properly or is set in the past.</Text>
      )}


      <Button
        bg={'green.400'}
        color={'white'}
        w="full"
        _hover={{
          bg: 'green.500',
        }}
        onClick={onSaveChanges}
        disabled={!isValidWeight || !!weightWarning || !validGoalDate || !validCalorieDelta}
      >
        I'm So Ready
      </Button>

    </React.Fragment>
  );
}